var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useState, useCallback, useMemo } from 'react';
import { translate } from '@utils/i18n';
import Table, { TableSize } from '@components/Table';
import SearchPanel from '@components/SearchPanel';
import Brushaway from '@components/Brushaway';
import { Button } from '@ui';
import { Loader, LoaderSizes } from '@components/Loader/Loader';
import camelToCapitalize from '@utils/functions/camelToCapitalize';
import './RecommendedComponentTypesImportModalStyles.scss';
import { useRecommendedComponentTypes, useImportComponentTypeMutation } from '../hooks/useComponentTypesMutations';
const namespace = 'features.settings.component_types.recommended';
function RecommendedComponentTypesImportModal({ isOpen, onClose, attachableTypes }) {
    const [searchText, setSearchText] = useState('');
    const { data: recommends = [], isLoading } = useRecommendedComponentTypes();
    const importMutation = useImportComponentTypeMutation();
    const attachableTypeLabels = useMemo(() => new Map(attachableTypes.map((option) => [option.value, option.label])), [attachableTypes]);
    const labelForAttachableType = useCallback((type) => attachableTypeLabels.get(type) || camelToCapitalize(type), [attachableTypeLabels]);
    const filteredRecommends = useMemo(() => {
        if (!searchText.trim()) {
            return recommends;
        }
        const lowerSearch = searchText.toLowerCase();
        return recommends.filter((recommend) => recommend.typeName.toLowerCase().includes(lowerSearch) ||
            recommend.typeNameEn.toLowerCase().includes(lowerSearch) ||
            recommend.category.toLowerCase().includes(lowerSearch) ||
            recommend.allowedAttachableTypes.some((type) => labelForAttachableType(type).toLowerCase().includes(lowerSearch)));
    }, [recommends, searchText, labelForAttachableType]);
    const handleImportRecommend = useCallback((recommend) => __awaiter(this, void 0, void 0, function* () {
        try {
            const requestData = {
                slug: recommend.slug,
                typeName: recommend.typeNameEn
            };
            yield importMutation.mutateAsync(requestData);
            onClose();
        }
        catch (_a) {
            // Error is already handled by mutation's onError
        }
    }), [importMutation, onClose]);
    const handleClose = () => {
        onClose();
    };
    const tableColumns = useMemo(() => [
        {
            label: translate('headers.type_name', { namespace }),
            dataKey: 'typeName',
            renderer: ({ item }) => React.createElement("span", null, item.typeName)
        },
        {
            label: translate('headers.category', { namespace }),
            dataKey: 'category',
            renderer: ({ item }) => React.createElement("span", null, item.category)
        },
        {
            label: translate('headers.allowed_attachable_types', { namespace }),
            dataKey: 'allowedAttachableTypes',
            renderer: ({ item }) => (React.createElement("div", { className: "attachable-types-badges" }, item.allowedAttachableTypes.map((type) => (React.createElement("span", { key: type, className: "qmb-tag--grey--readonly attachable-types-badge" }, labelForAttachableType(type))))))
        },
        {
            label: '',
            dataKey: 'actions',
            renderer: ({ item }) => (React.createElement(Button, { onClick: () => {
                    void handleImportRecommend(item);
                }, disabled: importMutation.isLoading }, translate('import_button', { namespace })))
        }
    ], [handleImportRecommend, importMutation.isLoading, labelForAttachableType]);
    return (React.createElement(Brushaway, { open: isOpen, title: translate('recommended_component_types_title', { namespace }), onClose: handleClose, customFooter: React.createElement(Button, { onClick: handleClose, disabled: importMutation.isLoading }, translate('close', { namespace: 'features.commons' })) },
        React.createElement("div", { className: "recommended-component-types-modal" },
            React.createElement("div", { className: "recommended-component-types-description" }, translate('recommended_component_types_description', { namespace })),
            React.createElement("div", { className: "filters__group--search recommended-component-types-search" },
                React.createElement(SearchPanel, { value: searchText, placeholder: translate('search', { namespace: 'filters' }), onChange: setSearchText })),
            React.createElement("div", { className: "recommended-component-types-table" },
                isLoading && (React.createElement("div", { className: "loading-center" },
                    React.createElement(Loader, { size: LoaderSizes.xlarge }))),
                !isLoading && filteredRecommends.length === 0 && (React.createElement("div", { className: "recommended-component-types-no-results-message" }, translate('no_recommends_available', { namespace }))),
                !isLoading && filteredRecommends.length > 0 && (React.createElement(Table, { columns: tableColumns, data: filteredRecommends, size: TableSize.Full }))))));
}
export default RecommendedComponentTypesImportModal;
